import { useEffect, useState } from 'react';
import { Text, Badge, Flex, Button, Icon, Tooltip } from '@scale/scaleui-radix';
import { PassAtKRunConfigDTO, PassAtKRunStatus, RunType } from '@scale/shared/types/passAtK';
import CopyArea from 'react-scale/dashboard.scale.com/components/AVTaxonomy/components/common/CopyArea';
import _ from 'lodash';
import { getStatusFromModelRuns } from 'react-scale/corp/views/CorpProjectGroupManager/views/Quality/PassAtK/utils/helpers';
import {
  RUN_STATUS_TO_COLOR,
  RUN_TYPE_TO_COLOR,
} from 'react-scale/corp/views/CorpProjectGroupManager/views/Quality/PassAtK/utils/constants';
import { SectionCard } from 'react-scale/corp/views/CorpProjectGroupManager/views/Quality/PassAtK/components/SectionCard';
import {
  getPassAtKRunConfigsQueryKey,
  getRunProgressesQueryKey,
  useCancelPassAtKRun,
  useRetryPassAtKRun,
  useGetRunProgresses,
} from 'react-scale/corp/views/CorpProjectGroupManager/views/Quality/PassAtK/api/apiClient';
import { useQueryClient } from '@tanstack/react-query-v4';
import { ProgressBar } from 'react-scale/corp/views/CorpProjectGroupManager/views/Quality/PassAtK/components/ProgressBar';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';

// Add optional runIds prop
type RunInfoProps = {
  runConfig: PassAtKRunConfigDTO;
  isLoading: boolean;
  runIds?: string[];
  linkToCompass?: boolean;
};

function RunInfo({ runConfig, isLoading, runIds, linkToCompass = true }: RunInfoProps) {
  const { _id, description, createdAt, modelRuns, runType, projectId } = runConfig;
  const status = getStatusFromModelRuns(modelRuns);
  const { mutate: cancelPassAtKRun } = useCancelPassAtKRun();
  const { mutate: retryPassAtKRun } = useRetryPassAtKRun();
  // Filter modelRuns by runIds if provided and non-empty
  const filteredModelRuns =
    runIds && runIds.length > 0 ? modelRuns.filter(mr => runIds.includes(mr._id)) : modelRuns;
  const { data: runProgresses } = useGetRunProgresses({
    projectId,
    runIds: filteredModelRuns.map(modelRun => modelRun._id),
  });

  const [isUpdating, setIsUpdating] = useState(false);
  useEffect(() => {
    if (isLoading) {
      setIsUpdating(true);
    } else {
      setIsUpdating(false);
    }
  }, [isLoading]);

  const queryClient = useQueryClient();

  return (
    <div className="space-y-4">
      <Flex align="center" justify="between" className="space-y-2">
        <div>
          <Text size="5" weight="medium">
            Pass@K Run {description ? `"${description}"` : '(No Description)'}
          </Text>
          <div className="flex gap-2 items-center mt-1">
            <Badge color={RUN_TYPE_TO_COLOR[runType]}>{_.capitalize(runType)}</Badge>
            <Badge size="2" color={RUN_STATUS_TO_COLOR[status]}>
              {_.capitalize(status)}
            </Badge>
          </div>
        </div>
        {(![
          PassAtKRunStatus.Cancelled,
          PassAtKRunStatus.Completed,
          PassAtKRunStatus.Error,
        ].includes(status) ||
          (runType === RunType.Continuous && status !== PassAtKRunStatus.Cancelled)) &&
          !isLoading && (
            <Button
              size="2"
              color="error"
              variant="solid"
              onClick={() => {
                setIsUpdating(true);
                cancelPassAtKRun(
                  { projectId, configId: _id },
                  {
                    onSuccess: () => {
                      queryClient.invalidateQueries({
                        queryKey: getPassAtKRunConfigsQueryKey(projectId),
                      });
                    },
                  },
                );
              }}
              disabled={isUpdating}
            >
              Cancel Run
            </Button>
          )}
      </Flex>

      <div className="flex gap-2 items-center">
        <div className="text-xs text-neutral-600">Created {formatDate(createdAt.toString())}</div>
        <CopyArea stringToCopy={_id} label="ID" className="text-sm" />
      </div>

      {filteredModelRuns.length > 0 && (
        <div className="space-y-2">
          {filteredModelRuns.map(modelRun => {
            const runProgress = runProgresses?.find(p => p.runId === modelRun._id);
            return (
              <SectionCard key={`${modelRun._id}-${modelRun.status}`}>
                <Flex direction="column" gap="1">
                  <Flex direction="row" gap="2" align="center" justify="between">
                    <Flex direction="row" gap="2" align="center">
                      <Text size="3">
                        <strong>{modelRun.modelName}</strong>
                      </Text>
                      <Badge className="mt-[2px]" color="accent">
                        Pass@{modelRun.k}
                      </Badge>
                      {modelRun.status === PassAtKRunStatus.Error && (
                        <Badge className="mt-[2px]" color="error">
                          Error
                        </Badge>
                      )}
                    </Flex>
                    {linkToCompass && modelRun.compassWorkflowId && (
                      <Tooltip content="Open in Compass">
                        <Button
                          size="1"
                          variant="ghost"
                          onClick={() => {
                            const baseUrl = window.location.origin;
                            const compassUrl = `${baseUrl}/corp/genai-ops-hub/compass/playground?workflow=${modelRun.compassWorkflowId}`;
                            window.open(compassUrl, '_blank');
                          }}
                        >
                          <Icon icon={faExternalLinkAlt} size="1" label="Open in Compass" />
                        </Button>
                      </Tooltip>
                    )}
                  </Flex>
                  <Text size="2" color="neutral">
                    The results for this model will be in the task metadata with key:
                  </Text>
                  <Text size="2" color="neutral">
                    <code>{modelRun.metadataKey}</code>
                  </Text>
                  {/* Progress for an individual model run */}
                  {runProgresses && (
                    <ProgressBar
                      progress={runProgress}
                      onRetry={() => {
                        setIsUpdating(true);
                        retryPassAtKRun(
                          {
                            projectId,
                            runId: modelRun._id,
                            resetRun: true,
                            allowedRunStatuses: [
                              PassAtKRunStatus.Error,
                              PassAtKRunStatus.Completed,
                            ],
                          },
                          {
                            onSuccess: () => {
                              queryClient.invalidateQueries({
                                queryKey: getPassAtKRunConfigsQueryKey(projectId),
                              });
                              queryClient.invalidateQueries({
                                queryKey: getRunProgressesQueryKey(
                                  projectId,
                                  modelRuns.map(modelRun => modelRun._id),
                                ),
                              });
                              setIsUpdating(false);
                              toast.success('Run retried successfully');
                            },
                            onError: () => {
                              toast.error('Failed to retry run');
                              setIsUpdating(false);
                            },
                          },
                        );
                      }}
                      onReactivate={() => {
                        setIsUpdating(true);
                        retryPassAtKRun(
                          {
                            projectId,
                            runId: modelRun._id,
                            resetRun: false,
                            allowedRunStatuses: [PassAtKRunStatus.Processing],
                          },
                          {
                            onSuccess: () => {
                              queryClient.invalidateQueries({
                                queryKey: getPassAtKRunConfigsQueryKey(projectId),
                              });
                              queryClient.invalidateQueries({
                                queryKey: getRunProgressesQueryKey(
                                  projectId,
                                  modelRuns.map(modelRun => modelRun._id),
                                ),
                              });
                              setIsUpdating(false);
                              toast.success('Run reactivated successfully');
                            },
                            onError: () => {
                              toast.error('Failed to reactivate run');
                              setIsUpdating(false);
                            },
                          },
                        );
                      }}
                      isUpdating={isUpdating}
                    />
                  )}
                </Flex>
              </SectionCard>
            );
          })}
        </div>
      )}
    </div>
  );
}

const DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
});
function formatDate(dateString: string) {
  return DATE_FORMAT.format(new Date(dateString)).replace(',', ' at');
}

export default RunInfo;
